// // import { Button } from "../../CustomButton";
// import { getDate, getTime } from "../../../../utils/helper";
// import { BookingsData } from "@/utils/apis/bookings_repository";
// import EventsChip from "../../table/EventsChip";
// import { getColor } from "../../table/StatusChip";

// interface BookingDetailsBodyProps {
//     booking: BookingsData;
// }

// const BookingDetailsBody = (booking: BookingDetailsBodyProps) => {
//     return (
//         <div className="whitespace-nowrap overflow-y-auto">
//             <Text as="h3">Booking Type</Text>
//             <div className="flex flex-row space-x-8 mt-6 items-center">
//                 <div className={`w-44 bg-primary py-3 px-3 flex justify-center text-white rounded-md`}>{booking.booking.bookingType} Booking</div>
//             </div>
//             <Text className="mt-10" as="h3">{booking.booking.bookingType === "Single" ? "Booking Date" : "Booking Validity"}</Text>
//             <Text className="mt-6 text-secondaryText">{booking.booking.bookingType === "Single" ? getDate(booking.booking.startTime) : "Valid From " + getDate(booking.booking.originalBooking?.startTime!) + " to " + getDate(booking.booking.originalBooking?.endTime!)}</Text>
//             <div className="flex flex-row space-x-8 items-center">
//                 <div className="flex flex-col">
//                     <Text as="h3">Start Time</Text>
//                     <Text className="text-secondaryText">{getTime(booking.booking.startTime)}</Text>
//                 </div>
//                 <div className="flex flex-col">
//                     <Text as="h3">End Time</Text>
//                     <Text className="text-secondaryText">{getTime(booking.booking.endTime)}</Text>
//                 </div>
//             </div>
//             <Text className="mt-10" as="h3">Current Status</Text>
//             <div className="mt-4">
//             <div className={`${getColor(booking.booking.status)} w-44 bg-primary py-3 px-3 flex justify-center rounded-md`}>{booking.booking.status} Booking</div>
//             </div>
//             <Text className="mt-10" as="h3">Events</Text>
//             <div className="flex flex-row space-x-8 mt-4 overflow-x-auto whitespace-nowrap items-center">{
//                 booking.booking.events === undefined ? <div>
//                     <Text className="text-secondaryText">No events Yet</Text>
//                 </div> : booking.booking.events.map((events) => {
//                     return (
//                         <EventsChip status={events.status} type={events.type} timestamp={events.timestamp} key={events.status} />
//                     )
//                 }, [])
//             }
//             </div>
//             <Text className="mt-10" as="h3">Available Plates</Text>
//             <div className="flex flex-row space-x-8 mt-6 items-center">{
//                 booking.booking.plate.map((plate) => {
//                     // make sure plates are not empty
//                     if (plate === "") {
//                         return null;
//                     }
//                     return (
//                         <Button variant="default" className="w-44 text-white">{plate}</Button>
//                     )
//                 }, [])
//             }</div>
//             <div className="mt-32">

//             </div>
//         </div>
//     );
// }

// export default BookingDetailsBody;

import React from 'react';
import { getDate, getTime, convertTimestampToDate, convertTimestampToTime } from "../../../../utils/helper";
import { BookingsData, EventData } from "@/utils/apis/bookings_repository";
import { getColor } from "../../chips/StatusChip";
import { describeBooking, ParkingTypeBadge } from "../../chips/BookingTypeChip";
import EvStationOutlined from "@mui/icons-material/EvStationOutlined";
import LocalParkingOutlined from "@mui/icons-material/LocalParkingOutlined";
import DateRangeOutlined from "@mui/icons-material/DateRangeOutlined";
import { Notice } from "../bookingForm";

interface BookingDetailsBodyProps {
    booking: BookingsData;
    // The series' days as the bookings list has them now, when this is a day of a series.
    seriesDays?: Pick<BookingsData, "startTime" | "endTime">[];
}

function getEventStatus(status: string, type: string) {
    if (status === "Arrived") return type;
    return status;
}

function getEventDotColor(status: string) {
    if (status === "Rejected" || status === "Update Rejected") return "bg-status-rejected";
    if (status.includes("Approved")) return "bg-status-approved";
    if (status === "Arrived") return "bg-status-arrived";
    if (status === "Pending") return "bg-status-pending";
    if (status === "Completed") return "bg-status-completed";
    if (status === "Expired") return "bg-status-expired";
    if (status === "Updated") return "bg-status-updated";
    if (status === "Exit") return "bg-status-exited";
    return "bg-gray-400";
}

function getEventBgColor(status: string) {
    if (status === "Rejected" || status === "Update Rejected") return "bg-status-rejected/10 border-status-rejected/20";
    if (status.includes("Approved")) return "bg-status-approved/10 border-status-approved/20";
    if (status === "Arrived") return "bg-status-arrived/10 border-status-arrived/20";
    if (status === "Pending") return "bg-status-pending/10 border-status-pending/20";
    if (status === "Completed") return "bg-status-completed/10 border-status-completed/20";
    if (status === "Expired") return "bg-status-expired/10 border-status-expired/20";
    if (status === "Updated") return "bg-status-updated/10 border-status-updated/20";
    if (status === "Exit") return "bg-status-exited/10 border-status-exited/20";
    return "bg-gray-50 border-gray-200";
}

// A label over its value, as the booking app lists a booking's details.
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
        <p className="text-xs text-app-secondary">{label}</p>
        <div className="mt-1 text-sm font-semibold text-app-text">{children}</div>
    </div>
);

const BookingDetailsBody: React.FC<BookingDetailsBodyProps> = ({ booking, seriesDays }) => {
    const isRejected = (status: string) => status === "Rejected" || status === "Update Rejected";
    const { space, days } = describeBooking(booking);
    const plates = Array.isArray(booking.plate) ? booking.plate.filter((plate) => plate !== "") : [];
    const coupon = booking.paymentId && booking.paymentId !== "complimentary" ? (booking.paymentId === "freeCoupon"
        ? `100% OFF${booking.couponName ? ` - (${booking.couponName})` : ""}`
        : booking.couponName ? `${booking.couponPercent}% OFF - (${booking.couponName})` : "Coupon not used") : null;
    const guest = booking.bookingType === "Guest";
    // The series runs from its first day to its last, as the list has them now (a day just cancelled is gone from
    // it, before the server updates the range saved on each day); without them, the saved range. A day ending at
    // midnight ends on the day before.
    const lastDayIso = (end: string) => end.endsWith("T00:00:00.000Z") ? new Date(Date.parse(end) - 86400000).toISOString() : end;
    const lastDay = (end: string) => getDate(lastDayIso(end));
    const listed = seriesDays?.length ? [...seriesDays].sort((a, b) => a.startTime.localeCompare(b.startTime)) : null;
    const series = guest || days !== "Multi-day" ? null :
        listed ? `${getDate(listed[0].startTime)} – ${lastDay(listed[listed.length - 1].endTime)} · ${listed.length} day${listed.length === 1 ? "" : "s"}` :
        booking.originalBooking?.startTime && booking.originalBooking?.endTime ?
            `${getDate(booking.originalBooking.startTime)} – ${lastDay(booking.originalBooking.endTime)}` : null;
    // A record spanning several days on its own (a valet booking, or a refused multi-day booking's own record).
    const spansDays = guest || (!series && Boolean(booking.endTime) && booking.startTime.slice(0, 10) !== lastDayIso(booking.endTime).slice(0, 10));

    return (
        <div className="pb-6">
            {/* Who it's for and what kind of booking, as the booking app's details show it */}
            <section className="mt-6 rounded-[20px] border border-app-border bg-white p-5">
                <div className="flex items-start gap-3">
                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${space === "EV" ? "bg-ev-container text-ev" : "bg-app-muted text-app-text"}`}>
                        {space === "EV" ? <EvStationOutlined aria-hidden /> : <LocalParkingOutlined aria-hidden />}
                    </div>
                    <div className="min-w-0 flex-1">
                        {booking.name && <p className="text-base font-semibold text-app-text">{booking.name}</p>}
                        {booking.email && <p className="truncate text-xs text-app-secondary">{booking.email}</p>}
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            <ParkingTypeBadge space={space} />
                            <span className={`${getColor(booking.status)} rounded-2xl px-2.5 py-1.5 text-xs font-bold`}>{booking.status}</span>
                            {space !== "Valet" && (
                                <span className="inline-flex items-center gap-1.5 rounded-2xl bg-app-muted px-2.5 py-1.5 text-xs font-bold text-app-text">
                                    <DateRangeOutlined aria-hidden sx={{ fontSize: 16 }} />
                                    {days === "Multi-day" ? "Part of a series" : "Single day"}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* When */}
            <section className="mt-6 space-y-4 rounded-[20px] border border-app-border bg-white p-5">
                <h3 className="text-lg font-bold text-app-text">When</h3>
                <div className="grid grid-cols-2 gap-4">
                    {/* A day of a series is one row, so it shows its own date, and the series it belongs to beside it.
                        A record spanning several days (a valet booking, or a refused multi-day booking's own record)
                        shows its range. */}
                    <Field label={spansDays ? "Dates" : "Date"}>
                        {spansDays ? getDate(booking.startTime!) + " - " + (guest ? getDate(booking.endTime!) : lastDay(booking.endTime)) : getDate(booking.startTime)}
                    </Field>
                    <Field label="Hours">{booking.tenant === "VALET" ? "5.30 PM" : getTime(booking.startTime)} – {getTime(booking.endTime)}</Field>
                    {series && <Field label="Series">{series}</Field>}
                </div>
                <p className="text-xs text-app-secondary">All times are Sydney time.</p>
            </section>

            {/* Vehicles */}
            <section className="mt-6">
                <h3 className="mb-3 text-lg font-bold text-app-text">Registered vehicles</h3>
                {plates.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {plates.map((plate, index) => <span key={index} className="rounded-lg bg-app-muted px-3 py-2 text-sm font-semibold text-app-text">{plate}</span>)}
                    </div>
                ) : (
                    <Notice>There's no vehicle on this booking.</Notice>
                )}
            </section>

            {/* Valet guests: who they are, and what they paid */}
            {(guest || coupon) && (
                <section className="mt-6 rounded-[20px] border border-app-border bg-white p-5">
                    <h3 className="mb-4 text-lg font-bold text-app-text">Guest details</h3>
                    <div className="grid grid-cols-2 gap-4">
                        {guest && <Field label="Cardholder unique ID">{booking.gallagherId}</Field>}
                        {guest && booking.tenant === "VALET" && <Field label="Customer name">{booking.name}</Field>}
                        {guest && booking.tenant === "VALET" && <Field label="Customer phone">{booking.phone}</Field>}
                        {guest && booking.venue && <Field label="Venue">{booking.venue}</Field>}
                        {coupon && <Field label="Coupon">{coupon}</Field>}
                    </div>
                </section>
            )}

            {/* Booking history */}
            <section className="mt-6">
                <h3 className="mb-3 text-lg font-bold text-app-text">Booking history</h3>
                {booking.events && booking.events.length > 0 ? (
                    <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-app-border" />

                        <div className="space-y-3">
                            {booking.events.map((event: EventData, index: number) => (
                                <div key={`${event.status}-${index}`} className="relative flex items-start gap-3 pl-6">
                                    {/* Dot */}
                                    <div className={`absolute left-0 top-3 w-[15px] h-[15px] rounded-full ${getEventDotColor(event.status)} border-2 border-white shadow-sm z-10`} />

                                    {/* Event card */}
                                    <div className={`flex-1 border rounded-xl p-3 ${getEventBgColor(event.status)}`}>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="font-semibold text-sm text-app-text">
                                                {getEventStatus(event.status, event.type)}
                                            </span>
                                            <span className="text-xs text-app-secondary">
                                                {convertTimestampToDate(event.timestamp)} at {convertTimestampToTime(event.timestamp)}
                                            </span>
                                        </div>

                                        {/* Description */}
                                        {event.description && (
                                            <div className={`mt-1.5 text-xs ${isRejected(event.status) ? "text-status-rejected" : "text-app-secondary"}`}>
                                                {isRejected(event.status) && <span className="font-medium">Reason: </span>}
                                                {event.description}
                                            </div>
                                        )}

                                        {/* Show fallback for rejected with no reason */}
                                        {isRejected(event.status) && !event.description && (
                                            <div className="mt-1.5 text-xs text-status-rejected italic">
                                                No reason provided
                                            </div>
                                        )}

                                        {/* Violation */}
                                        {event.violation && (
                                            <div className="mt-1.5 text-xs text-status-rejected">
                                                <span className="font-medium">Violation: {event.violation.title}</span>
                                                {event.violation.description && (
                                                    <span className="block mt-0.5">{event.violation.description}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-app-secondary">Nothing has happened to this booking yet.</p>
                )}
            </section>
        </div>
    );
}

export default BookingDetailsBody;
